<template>
  <div class="space-y-6 lg:space-y-6">
    <!-- Header -->
    <div>
      <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {{ headerTitle }}
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ headerDescription }}
          </p>
        </div>
        <div class="flex gap-2 xl:flex-none w-full xl:w-auto">
          <woot-button
            variant="smooth"
            color-scheme="success"
            size="small"
            v-if="mode === 'library'"
            class="w-full xl:w-auto"
            @click="openCreateLibrary"
          >
            <lucide-icon name="plus" size="18" class="mr-2" />
            {{ $t('IMAGE_LIBRARY.ADD_FOLDER') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="success"
            size="small"
            v-else
            class="w-full xl:w-auto"
            @click="openUpload"
          >
            <lucide-icon name="upload" size="18" class="mr-2" />
            {{ $t('IMAGE_LIBRARY.UPLOAD_IMAGES') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="flex flex-col xl:flex-row gap-4">
      <div class="flex-1 relative">
        <div class="relative">
          <input
            v-model="q"
            type="text"
            :placeholder="`🔍 ${$t('IMAGE_LIBRARY.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="onSearch"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3 xl:flex-row xl:flex-wrap">
        <div class="relative w-full xl:w-auto xl:flex-1">
          <fluent-icon icon="circle" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
          <select 
            v-model="selectedCategory" 
            class="pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 w-full xl:w-48 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('IMAGE_LIBRARY.ALL_CATEGORIES') }}</option>
            <option value="uncategorized">{{ $t('BUSINESS_DATA.FAQS.UNCATEGORIZED') }}</option>
            <option 
              v-for="category in businessCategories" 
              :key="category.id" 
              :value="category.name"
            >
              {{ category.name }}
            </option>
          </select>
          <fluent-icon icon="chevron-down" size="14" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
        </div>
      </div>
      <div class="flex items-center gap-2 ml-auto flex-wrap" v-if="mode === 'item'">
        <woot-button
          variant="smooth"
          color-scheme="secondary"
          size="small"
          @click="exportData()"
        >
          <lucide-icon name="download" size="14" class="mr-2" />
          XLSX
        </woot-button>
      <woot-button
        variant="smooth"
        color-scheme="secondary"
        size="small"
        @click="triggerImport"
      >
        <lucide-icon name="upload" size="14" class="mr-2" />
        XLSX/CSV
      </woot-button>
      <input
        ref="fileInput"
        type="file"
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        class="hidden"
        @change="onFileSelected"
      />
    </div>
  </div>

    <!-- Breadcrumb -->
    <div v-if="mode === 'item'" class="text-sm text-gray-600">
      <router-link :to="basePath" class="text-indigo-600 hover:underline">{{ $t('IMAGE_LIBRARY.TITLE') }}</router-link>
      <span class="mx-1">›</span>
      <span>{{ currentLibrary?.name }}</span>
    </div>

    <BulkActionsBar
      v-if="hasItemSelection"
      class="mb-4"
      :selected-count="selectedItemCount"
      :is-all-selected-on-page="isAllItemsSelectedOnPage"
      :bulk-action="bulkItemActionLoading"
      :is-bulk-deleting="isBulkDeletingItems"
      @toggle-select-page="toggleSelectItemsOnPage"
      @clear-selection="clearItemSelection"
      @activate="() => bulkUpdateItemStatus(true)"
      @deactivate="() => bulkUpdateItemStatus(false)"
      @delete="bulkDeleteItems"
    />

    <!-- Grid duy nhất -->
    <ImageGrid
      :mode="mode"
      :items="items"
      :is-loading="isLoading"
      :page="page"
      :total="total"
      :page-size="12"
      :enable-selection="mode === 'item'"
      :selected-ids="mode === 'item' ? selectedItemIds : []"
      @card-click="onCardClick"
      @page-change="onPageChange"
      @action="onAction"
      @toggle-select="onToggleSelect"
    />

    <!-- Help section -->
    <div v-if="mode==='library'" class="bg-purple-50 border border-purple-200 rounded-lg p-4">
      <div class="font-medium text-purple-900 mb-1">{{ $t('IMAGE_LIBRARY_HELP.TITLE') }}</div>
      <p class="text-sm text-purple-800 mb-2">{{ $t('IMAGE_LIBRARY_HELP.INTRO') }}</p>
      <ul class="list-disc pl-5 text-sm text-purple-900 space-y-1">
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.CREATE_AND_ACTIVE') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.UPLOAD_AND_TAGS') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.TOGGLE_VISIBILITY') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.COPY_URL') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.AI_USAGE') }}</li>
        <li>{{ $t('IMAGE_LIBRARY_HELP.POINTS.LIMIT') }}</li>
      </ul>
    </div>

    <!-- Modals -->
    <LibraryModal
      :show="showLibraryModal"
      :account-id="accountId"
      :library="editingLibrary"
      :business-categories="businessCategories"
      @close="closeLibraryModal"
      @saved="handleLibrarySaved"
      @category-created="onCategoryCreated"
    />
    <ItemUploadModal
      :show="showUploadModal"
      :account-id="accountId"
      :library-id="libraryId"
      @close="handleUploadClosed"
      @uploaded="handleUploadCompleted"
    />
    <ItemEditModal
      :show="showEditItemModal"
      :account-id="accountId"
      :library-id="libraryId"
      :item="editingItem"
      @close="closeItemModal"
      @saved="handleItemSaved"
    />
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables'
import { frontendURL } from 'dashboard/helper/URLHelper'
import ImageGrid from './ImageGrid.vue'
import LibraryModal from './LibraryModal.vue'
import ItemUploadModal from './ItemUploadModal.vue'
import ItemEditModal from './ItemEditModal.vue'
import BulkActionsBar from './business-data/BulkActionsBar.vue'

export default {
  name: 'ImageLibraryTab',
  components: { ImageGrid, LibraryModal, ItemUploadModal, ItemEditModal, BulkActionsBar },
  data() {
    return {
      isLoading: false,
      mode: 'library', // 'library' | 'item'
      items: [],
      total: 0,
      page: 1,
      q: '',
      sort: 'created_desc',
      selectedCategory: '',
      sortOptions: [
        { value: 'name_asc', label: this.$t('IMAGE_LIBRARY.SORT.NAME_ASC') },
        { value: 'created_desc', label: this.$t('IMAGE_LIBRARY.SORT.NEWEST') },
        { value: 'order_asc', label: this.$t('IMAGE_LIBRARY.SORT.CUSTOM') },
        { value: 'images_desc', label: this.$t('IMAGE_LIBRARY.SORT.MOST_IMAGES') },
      ],
      currentLibrary: null,
      showLibraryModal: false,
      editingLibrary: null,
      showUploadModal: false,
      showEditItemModal: false,
      editingItem: null,
      businessCategories: [],
      selectedItemIds: [],
      isBulkDeletingItems: false,
      bulkItemActionLoading: '',
      libraryModalMode: 'create',
      itemModalMode: 'edit',
    }
  },
  computed: {
    accountId() { return this.$store.getters.getCurrentAccountId },
    basePath() { return frontendURL(`accounts/${this.accountId}/settings/ai-settings`) },
    libraryId() { return this.$route.params.libraryId },
    headerTitle() {
      if (this.mode === 'library') {
        return this.$t('IMAGE_LIBRARY.TITLE')
      }
      return this.currentLibrary?.name || this.$t('IMAGE_LIBRARY.TITLE')
    },
    headerDescription() {
      return this.mode === 'library'
        ? this.$t('IMAGE_LIBRARY.DESCRIPTION_LIBRARY')
        : this.$t('IMAGE_LIBRARY.DESCRIPTION_ITEMS')
    },
    selectedItemCount() {
      return this.selectedItemIds.length
    },
    hasItemSelection() {
      return this.mode === 'item' && this.selectedItemCount > 0
    },
    isAllItemsSelectedOnPage() {
      if (this.mode !== 'item' || !this.items || !this.items.length) {
        return false
      }
      const pageIds = this.items.map(item => item.id)
      return pageIds.every(id => this.selectedItemIds.includes(id))
    },
  },
  watch: {
    '$route.params.libraryId': {
      handler() { this.syncModeFromRoute(); this.load() }, immediate: true
    }
  },
  methods: {
    syncModeFromRoute() {
      this.mode = this.libraryId ? 'item' : 'library'
      this.page = 1
      this.selectedItemIds = []
    },
    async load() {
      if (!this.accountId) return
      this.isLoading = true
      try {
        await this.loadBusinessCategories()
        if (this.mode === 'library') await this.loadLibraries()
        else await this.loadItems()
      } finally {
        this.isLoading = false
      }
    },
    async loadBusinessCategories() {
      try {
        const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/business_categories`, {
          params: { category_type: 'image' }
        })
        this.businessCategories = data.business_categories || []
      } catch (error) {
        console.error('Failed to load business categories:', error)
        this.businessCategories = []
      }
    },
    async loadLibraries() {
      const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries`, {
        params: { 
          page: this.page, 
          per_page: 12, 
          q: this.q, 
          sort: this.sort, 
          category: this.selectedCategory || undefined,
          include_business_category: true 
        }
      })
      this.items = data.image_libraries || []
      this.total = data.meta?.total_count || 0
    },
    async loadItems() {
      const { data } = await window.axios.get(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/by_library`, {
        params: { page: this.page, per_page: 12, search: this.q, sort: this.sort }
      })
      this.items = data.image_items || []
      this.total = data.meta?.total_count || 0
      this.currentLibrary = data.library || null
      const availableIds = this.items.map(item => item.id)
      this.selectedItemIds = this.selectedItemIds.filter(id => availableIds.includes(id))
    },
    onPageChange(p) { this.page = p; this.load() },
    onSearch() { clearTimeout(this.searchTimer); this.searchTimer = setTimeout(() => { this.page = 1; this.load() }, 300) },
    onFilterChange() { this.page = 1; this.load() },
    onCardClick(item) {
      if (this.mode === 'library') this.$router.push({ params: { libraryId: item.id } })
    },
    onToggleSelect(event) {
      if (this.mode !== 'item') return
      const { id, checked } = event
      if (!id) return
      if (checked) {
        if (!this.selectedItemIds.includes(id)) {
          this.selectedItemIds = [...this.selectedItemIds, id]
        }
      } else {
        this.selectedItemIds = this.selectedItemIds.filter(existingId => existingId !== id)
      }
    },
    toggleSelectItemsOnPage() {
      if (this.mode !== 'item') return
      const pageIds = this.items.map(item => item.id)
      if (this.isAllItemsSelectedOnPage) {
        this.selectedItemIds = this.selectedItemIds.filter(id => !pageIds.includes(id))
      } else {
        const combined = new Set([...this.selectedItemIds, ...pageIds])
        this.selectedItemIds = Array.from(combined)
      }
    },
    clearItemSelection() {
      this.selectedItemIds = []
    },
    handleLibrarySaved() {
      this.closeLibraryModal()
      this.load()
      const key =
        this.libraryModalMode === 'edit'
          ? 'IMAGE_LIBRARY.LIBRARY_UPDATE_SUCCESS'
          : 'IMAGE_LIBRARY.LIBRARY_CREATE_SUCCESS'
      useAlert(this.$t(key), { type: 'success' })
    },
    closeLibraryModal() {
      this.showLibraryModal = false
      this.editingLibrary = null
      this.libraryModalMode = 'create'
    },
    handleItemSaved() {
      this.closeItemModal()
      this.load()
      useAlert(this.$t('IMAGE_LIBRARY.ITEM_UPDATE_SUCCESS'), { type: 'success' })
    },
    closeItemModal() {
      this.showEditItemModal = false
      this.editingItem = null
    },
    handleUploadClosed() {
      this.showUploadModal = false
    },
    handleUploadCompleted() {
      this.handleUploadClosed()
      this.load()
      useAlert(this.$t('IMAGE_LIBRARY.UPLOAD_SUCCESS'), { type: 'success' })
    },
    async bulkDeleteItems() {
      if (!this.selectedItemCount) return
      const confirmMessage = this.$t('IMAGE_LIBRARY.BULK_DELETE_CONFIRM', { count: this.selectedItemCount })
      if (!window.confirm(confirmMessage)) {
        return
      }
      if (!this.accountId || !this.libraryId) return
      this.isBulkDeletingItems = true
      try {
        const count = this.selectedItemCount
        await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/bulk_delete`, {
          ids: this.selectedItemIds
        })
        useAlert(this.$t('IMAGE_LIBRARY.BULK_DELETE_SUCCESS', { count }), {
          type: 'success',
        })
        this.clearItemSelection()
        this.load()
      } catch (error) {
        useAlert(this.$t('IMAGE_LIBRARY.BULK_DELETE_ERROR'), {
          type: 'error',
        })
      } finally {
        this.isBulkDeletingItems = false
      }
    },
    async bulkUpdateItemStatus(isActive) {
      if (!this.selectedItemCount) return
      if (!this.accountId || !this.libraryId) return
      const actionKey = isActive ? 'activate' : 'deactivate'
      this.bulkItemActionLoading = actionKey
      const count = this.selectedItemCount
      try {
        await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/bulk_update_status`, {
          ids: this.selectedItemIds,
          is_active: isActive
        })
        const messageKey = isActive ? 'IMAGE_LIBRARY.BULK_ACTIVATE_SUCCESS' : 'IMAGE_LIBRARY.BULK_DEACTIVATE_SUCCESS'
        useAlert(this.$t(messageKey, { count }), { type: 'success' })
        this.clearItemSelection()
        this.load()
      } catch (error) {
        const errorKey = isActive ? 'IMAGE_LIBRARY.BULK_ACTIVATE_ERROR' : 'IMAGE_LIBRARY.BULK_DEACTIVATE_ERROR'
        useAlert(this.$t(errorKey), { type: 'error' })
      } finally {
        this.bulkItemActionLoading = ''
      }
    },
    async onAction({ type, item }) {
      try {
        if (this.mode === 'library') {
          if (type === 'delete') {
            if (!this.confirmDelete()) return
            await window.axios.delete(`/api/v1/accounts/${this.accountId}/image_libraries/${item.id}`)
            useAlert(this.$t('IMAGE_LIBRARY.LIBRARY_DELETE_SUCCESS'), {
              type: 'success',
            })
            this.load()
          } else if (type === 'edit') {
            this.libraryModalMode = 'edit'
            this.editingLibrary = item
            this.showLibraryModal = true
          }
        } else if (type === 'delete') {
          if (!this.confirmDelete()) return
          await window.axios.delete(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/${item.id}`)
          useAlert(this.$t('IMAGE_LIBRARY.ITEM_DELETE_SUCCESS'), {
            type: 'success',
          })
          this.load()
        } else if (type === 'toggle') {
          await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/${item.id}/toggle_status`)
          this.load()
        } else if (type === 'rename' || type === 'edit') {
          this.itemModalMode = 'edit'
          this.editingItem = item
          this.showEditItemModal = true
        } else if (type === 'copy_url' && (item.file_url || item.download_url)) {
          await navigator.clipboard.writeText(item.file_url || item.download_url)
          useAlert(this.$t('IMAGE_LIBRARY.COPY_URL_SUCCESS'), {
            type: 'info',
          })
        }
      } catch (e) {
        useAlert(this.$t('GENERAL.ERROR'), { type: 'error' })
      }
    },
    confirmDelete() {
      const key = 'IMAGE_LIBRARY.ACTION_CONFIRM.DELETE'
      const message = typeof this.$te === 'function' && this.$te(key)
        ? this.$t(key)
        : 'Are you sure you want to delete this item?'
      return window.confirm(message)
    },
    openCreateLibrary() {
      this.libraryModalMode = 'create'
      this.editingLibrary = null
      this.showLibraryModal = true
    },
    openUpload() { this.showUploadModal = true },
    async exportData(format = 'xlsx') {
      if (!this.accountId || !this.libraryId) return
      const params = new URLSearchParams()
      if (this.q) params.append('search', this.q)
      if (this.selectedCategory) params.append('category', this.selectedCategory)
      params.append('format', format)
      const url = `/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/export?${params.toString()}`
      const mimeType = format === 'csv'
        ? 'text/csv;charset=utf-8;'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const extension = format === 'csv' ? 'csv' : 'xlsx'
      try {
        const res = await window.axios.get(url, { responseType: 'blob' })
        const blob = new Blob([res.data], { type: mimeType })
        const link = document.createElement('a')
        const objectUrl = URL.createObjectURL(blob)
        link.href = objectUrl
        link.setAttribute('download', `image_items_${Date.now()}.${extension}`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
        this.$store.dispatch('setBanner', { type: 'success', message: this.$t('IMAGE_LIBRARY.EXPORT_SUCCESS') })
      } catch (e) {
        this.$store.dispatch('setBanner', { type: 'error', message: this.$t('GENERAL.ERROR') })
      }
    },
    triggerImport() {
      this.$refs.fileInput?.click()
    },
    async onFileSelected(e) {
      const file = e.target.files?.[0]
      if (!file || !this.accountId || !this.libraryId) return
      
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (!['csv', 'xlsx'].includes(extension || '')) {
        this.$store.dispatch('setBanner', { type: 'error', message: 'Chỉ hỗ trợ file CSV hoặc XLSX' })
        e.target.value = ''
        return
      }
      
      const form = new FormData()
      form.append('import_file', file)
      try {
        const url = `/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/import`
        const res = await window.axios.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
        
        if (res.data.error) {
          this.$store.dispatch('setBanner', { type: 'error', message: `Lỗi import: ${res.data.error}` })
          return
        }
        
        const { created = 0, updated = 0, failed_count = 0, failed = [] } = res.data || {}
        let msg = `Import hoàn thành: ${created} tạo mới, ${updated} cập nhật, ${failed_count} lỗi`
        
        if (failed_count > 0 && failed.length > 0) {
          const errorDetails = failed.slice(0, 3).map(f => `${f.title || 'Unknown'}: ${f.errors || 'Unknown error'}`).join('; ')
          msg += `\nChi tiết lỗi: ${errorDetails}${failed.length > 3 ? '...' : ''}`
        }
        
        this.$store.dispatch('setBanner', { 
          type: failed_count > 0 ? 'warning' : 'success', 
          message: msg 
        })
        this.load()
      } catch (err) {
        console.error('Import error:', err)
        const errorMsg = err.response?.data?.error || err.message || 'Lỗi không xác định'
        this.$store.dispatch('setBanner', { type: 'error', message: `Lỗi import: ${errorMsg}` })
      } finally {
        e.target.value = ''
      }
    },
    
    onCategoryCreated(newCategory) {
      this.businessCategories.push(newCategory);
    },
  }
}
</script>
